import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import type { SiteStatus, StudyStatus } from "../../api/statisticsApi";
import { cn } from "../../lib/utils";
import { Count, fmtDate, LINK, Message, Pill, ROW, Section, StatCard, Table, TD, TH } from "./statsUi";

// Status ośrodka starszy niż tyle dni może już nie odpowiadać rzeczywistości
const STALE_DAYS = 90;

const isStale = (iso: string | null) =>
  !!iso && (Date.now() - new Date(iso).getTime()) / 86_400_000 > STALE_DAYS;

const recruitmentPct = (s: SiteStatus) =>
  s.declared > 0 ? `${Math.round((s.randomized * 100) / s.declared)}%` : "—";

const StatusCells = ({ s }: { s: SiteStatus }) => (
  <>
    <TD right>{s.declared}</TD>
    <TD right>{s.afterScreening}</TD>
    <TD right>{s.screenFailure}</TD>
    <TD right className="font-medium">{s.randomized}</TD>
    <TD right className="text-[var(--sc-muted)]">{recruitmentPct(s)}</TD>
    <TD right>{s.duringTreatment}</TD>
    <TD right>{s.duringFollowUp}</TD>
    <TD right>{s.prematureCompletion}</TD>
    <TD right>{s.completedStudy}</TD>
    <TD right><Count n={s.reportedSAE} tone="red" /></TD>
    <TD right><Count n={s.protocolDeviations} tone="amber" /></TD>
  </>
);

const AsOf = ({ iso }: { iso: string | null }) =>
  isStale(iso)
    ? <Pill tone="amber" title={`Ostatni status starszy niż ${STALE_DAYS} dni`}>{fmtDate(iso)}</Pill>
    : <span className="whitespace-nowrap">{fmtDate(iso)}</span>;

export const StudiesTab = ({
  data, isLoading, study,
}: { data?: StudyStatus[]; isLoading: boolean; study: string }) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading) return <Message>Ładowanie…</Message>;
  if (!data) return <Message>Nie udało się pobrać danych</Message>;

  const rows = data.filter(s => !study || s.studyName === study);
  const total = (f: (s: SiteStatus) => number) => rows.reduce((a, r) => a + f(r.status), 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Randomizowani" value={total(s => s.randomized)} hint={`zadeklarowanych: ${total(s => s.declared)}`} />
        <StatCard label="W leczeniu / w obserwacji" value={`${total(s => s.duringTreatment)} / ${total(s => s.duringFollowUp)}`} />
        <StatCard label="SAE" value={total(s => s.reportedSAE)} tone="red" />
        <StatCard label="Odchylenia od protokołu" value={total(s => s.protocolDeviations)} tone="amber" />
      </div>

      <Section
        title="Status badań"
        description={<>
          Stan z ostatniego raportu każdego ośrodka („Status ośrodka”), zsumowany dla badania. Kolumna „Dane z” to
          najstarszy z tych raportów; na bursztynowo, gdy jest starszy niż {STALE_DAYS} dni. Kliknij wiersz, aby zobaczyć ośrodki.
        </>}
      >
        {rows.length === 0 ? <Message>Brak raportów z wypełnionym statusem ośrodka</Message> : (
          <Table head={<>
            <TH>Badanie</TH><TH right>Ośrodki</TH><TH right>Zadekl.</TH><TH right>Po skriningu</TH>
            <TH right>Niepow. skriningu</TH><TH right>Randomiz.</TH><TH right>Rekrutacja</TH><TH right>W leczeniu</TH>
            <TH right>W obserwacji</TH><TH right>Zakończ. przedwcz.</TH><TH right>Ukończyli</TH><TH right>SAE</TH>
            <TH right>Odchylenia</TH><TH>Dane z</TH>
          </>}>
            {rows.map(s => {
              const open = expanded === s.studyId;
              return (
                <Fragment key={s.studyId}>
                  <tr
                    onClick={() => setExpanded(open ? null : s.studyId)}
                    className={cn(ROW, "cursor-pointer hover:bg-[var(--sc-bg)]", open && "bg-[var(--sc-bg)]")}
                  >
                    <TD className="font-medium">{s.studyName}</TD>
                    <TD right title="ośrodki z wypełnionym statusem / wszystkie z raportami">
                      {s.resortsWithData}/{s.resortCount}
                    </TD>
                    <StatusCells s={s.status} />
                    <TD><AsOf iso={s.oldestAsOf} /></TD>
                  </tr>
                  {open && s.resorts.map(r => (
                    <tr key={r.resortId} className={cn(ROW, "bg-[var(--sc-bg)]")}>
                      <TD className="pl-4">
                        <Link to={`/reports/${r.reportId}`} className={LINK} title={r.reportName}>{r.resortName}</Link>
                      </TD>
                      <TD />
                      <StatusCells s={r.status} />
                      <TD><AsOf iso={r.asOf} /></TD>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </Table>
        )}
      </Section>
    </div>
  );
};
